import { useId } from 'react';
import styles from './Select.module.css';

interface SelectOption<V extends string = string> {
  value: V;
  label: string;
}

interface SelectProps<V extends string = string> {
  label?: string;
  value: V | '';
  options: readonly SelectOption<V>[];
  placeholder?: string;
  onChange: (value: V | '') => void;
  size?: 'sm' | 'md';
  error?: string;
  disabled?: boolean;
}

export function Select<V extends string = string>({
  label,
  value,
  options,
  placeholder,
  onChange,
  size = 'md',
  error,
  disabled,
}: SelectProps<V>) {
  const generatedId = useId();
  const errorId = error ? `${generatedId}-error` : undefined;

  return (
    <div className={styles.wrapper}>
      {label && (
        <label htmlFor={generatedId} className={styles.label}>
          {label}
        </label>
      )}
      <div className={styles.selectWrapper}>
        <select
          id={generatedId}
          className={`${styles.select} ${size === 'sm' ? styles.sm : styles.md} ${error ? styles.selectError : ''}`}
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          onChange={(e) => onChange(e.target.value as V | '')}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg className={styles.chevron} viewBox="0 0 12 8" aria-hidden="true">
          <path
            d="M1 1.5L6 6.5L11 1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {error && (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
